import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import fakeRequest from 'src/utils/fakeRequest';
import useAuth from 'src/hooks/useAuth';
import Icon from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { REST_API } from 'src/constants/DefaultValues';
import axios from 'axios';

MayuraReceiverAddEditForm.propTypes = {
  isEdit: PropTypes.bool,
  currentUser: PropTypes.object,
  onClose: PropTypes.func,
};

export default function MayuraReceiverAddEditForm({ isEdit, currentUser, onClose }) {
  console.log('currentUser', currentUser)
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const { user } = useAuth()
  const [loading, setLoading] = useState(false);
  const taxTypeOptions = ['GST', 'URP'];

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${REST_API}branches/get-all-active-branches`);
      setBranchList(res.data.status === 1 ? res.data.data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranchList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);
  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const ReceiverSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, 'Only alphanumeric characters and single spaces allowed')
      .required('Name is required'),

    taxType: Yup.string()
      .oneOf(['GST', 'URP'], 'Invalid tax type')
      .required('Tax type is required'),

    gst: Yup.string()
      .when('taxType', {
        is: 'GST',
        then: Yup.string()
          .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
          .required('GST No is required'),
        otherwise: Yup.string().nullable(),
      }),

    urp: Yup.string()
      .when('taxType', {
        is: 'URP',
        then: Yup.string()
          .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
          .required('URP No is required'),
        otherwise: Yup.string().nullable(),
      }),

    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, 'Enter valid 10-digit mobile number')
      .required('Mobile No is required'),

    branchLocation: Yup.object()
      .nullable()
      .required('Branch is required'),

    city: Yup.string()
      .trim()
      .matches(/^(?=.*[a-zA-Z])[a-zA-Z\s]+$/, 'Only letters and spaces allowed')
      .required('City is required'),

    landline: Yup.string()
      .matches(/^[0-9]{6,12}$/, 'Only digits, min 6 and max 12 digits')
      .nullable(),

    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required')
      .nullable(false),

    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .max(30, 'Password must not exceed 30 characters')
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
        'Must include uppercase, lowercase, number, and special character'
      )
      .required('Password is required'),

    address: Yup.string()
      .trim()
      .matches(/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s,-]{1,100}$/, 'No special characters except , - allowed')
      .required('Address is required'),

    state: Yup.string()
      .trim()
      .matches(/^(?=.*[a-zA-Z])[a-zA-Z\s]{2,30}$/, 'Only letters and spaces allowed')
      .required('State is required'),

    landmark: Yup.string()
      .trim()
      .matches(/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s,-]{1,50}$/, 'No special characters except , - allowed')
      .required('Landmark is required'),

    postalCode: Yup.string()
      .matches(/^[0-9]{5,10}$/, 'Only digits between 5 to 10 length')
      .nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customer_id: currentUser?.customer_id || '',
      name: currentUser?.user_name || '',
      mobile: currentUser?.phone_number || '',
      taxType: currentUser?.urp ? 'URP' : 'GST',
      gst: currentUser?.gst || '',
      urp: currentUser?.urp || '',
      city: currentUser?.city || '',
      branchLocation: currentUser?.branch_id
        ? branchList.find((branch) => branch.id === currentUser.branch_id) || null
        : null,
      address: currentUser?.address || '',
      state: currentUser?.state || '',
      landmark: currentUser?.landmark || '',
      postalCode: currentUser?.postal_Code || '',
      landline: currentUser?.land_line || '',
      email: currentUser?.email || '',
      password: currentUser?.non_hashed_password || '',
      isSelf: currentUser?.is_self || false,
      customer_type: 2,
    },

    validationSchema: ReceiverSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        await fakeRequest(500);

        const payload = {
          ...values,
          addedBy: user?.id
        };

        if (isEdit && currentUser?.user_id) {
          payload.user_id = currentUser.user_id;
          payload.customer_id = values.customer_id;
          payload.updated_by = user?.id;
        }

        const apiEndpoint = isEdit
          ? `${REST_API}customers/update-customer`
          : `${REST_API}customers/add-customer`;

        const res = await axios.post(apiEndpoint, payload);
        if (res.data.status === 1) {
          enqueueSnackbar(isEdit ? 'Receiver updated successfully' : 'Receiver created successfully', { variant: 'success' });
          resetForm();
          setSubmitting(false);
          onClose();
        } else if (res.data.status === 2) {
          enqueueSnackbar(res.data.message || 'No changes were made to the receiver profile', { variant: 'info' });
          setSubmitting(false);
          onClose();
        } else {
          enqueueSnackbar(res.data.message || 'Operation failed', { variant: 'error' });
        }
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        setErrors(error);
      }
    }

  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isSubmitting
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 3, boxShadow: 'none' }}>
          <Stack container gap={2} direction="column">
            <Box sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isSelf}
                    onChange={(e) => setFieldValue('isSelf', e.target.checked)}
                  />
                }
                label="Self"
              />
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                // label="Consignee"
                value="Consignee"
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                fullWidth
                label="Name"
                {...getFieldProps('name')}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <TextField
                fullWidth
                label="Mobile No"
                {...getFieldProps('mobile')}
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={touched.mobile && errors.mobile}
              />
              <TextField
                fullWidth
                label="Landline No"
                {...getFieldProps('landline')}
                error={touched.landline && Boolean(errors.landline)}
                helperText={touched.landline && errors.landline}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                label="Email"
                {...getFieldProps('email')}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <Autocomplete
                fullWidth
                options={taxTypeOptions}
                value={values.taxType || 'GST'}
                onChange={(_, newValue) => {
                  setFieldValue('taxType', newValue || 'GST');
                  if (newValue === 'GST') {
                    setFieldValue('urp', '');
                  } else if (newValue === 'URP') {
                    setFieldValue('gst', '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tax Type"
                    error={touched.taxType && Boolean(errors.taxType)}
                    helperText={touched.taxType && errors.taxType}
                  />
                )}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              {values.taxType === 'GST' ? (
                <TextField
                  fullWidth
                  label="GST No"
                  {...getFieldProps('gst')}
                  error={touched.gst && Boolean(errors.gst)}
                  helperText={touched.gst && errors.gst}
                />
              ) : (
                <TextField
                  fullWidth
                  label="URP No"
                  {...getFieldProps('urp')}
                  error={touched.urp && Boolean(errors.urp)}
                  helperText={touched.urp && errors.urp}
                />
              )}
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <Autocomplete
                options={branchList}
                fullWidth
                loading={loading}
                getOptionLabel={(option) =>
                  `${option.branch_address} - ${option.branch_code}`
                }
                value={values.branchLocation}
                onChange={(_, newValue) =>
                  setFieldValue('branchLocation', newValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch Added"
                    fullWidth
                    error={
                      touched.branchLocation && Boolean(errors.branchLocation)
                    }
                    helperText={touched.branchLocation && errors.branchLocation}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Address"
                {...getFieldProps('address')}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />

              <TextField
                fullWidth
                label="Landmark"
                {...getFieldProps('landmark')}
                error={touched.landmark && Boolean(errors.landmark)}
                helperText={touched.landmark && errors.landmark}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignContent={'center'}
            >
              <TextField
                fullWidth
                label="City"
                {...getFieldProps('city')}
                error={touched.city && Boolean(errors.city)}
                helperText={touched.city && errors.city}
              />
              <TextField
                fullWidth
                label="State"
                {...getFieldProps('state')}
                error={touched.state && Boolean(errors.state)}
                helperText={touched.state && errors.state}
              />

              <TextField
                fullWidth
                label="Postal Code"
                {...getFieldProps('postalCode')}
                error={touched.postalCode && Boolean(errors.postalCode)}
                helperText={touched.postalCode && errors.postalCode}
              />
            </Stack>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  sx={{ boxShadow: 'none' }}
                  startIcon={
                    <Stack
                      className={
                        isEdit ? 'ic--twotone-edit' : 'lets-icons--add-duotone'
                      }
                      sx={{ width: 24, height: 24 }}
                    />
                  }
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {isEdit ? 'Update Receiver' : 'Add Receiver'}
                </LoadingButton>
              </Stack>
            </Grid>
          </Stack>
        </Card>
      </Form>
    </FormikProvider>
  )
}
